/*
 * Tag cloud
 *  - Tooltips on the tag links: hover/focus on desktop (CSS only); tap to
 *    toggle on touch devices, tap-off or Escape to close.
 *  - Lightbox on the button: opens a modal with the nutritional-label image,
 *    closed via the X, the backdrop, or Escape.
 */
class TagCloud extends HTMLElement {
  connectedCallback() {
    this.setupTooltips();
    this.setupLightbox();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onLightboxKeydown);
  }

  /* Tooltips --------------------------------------------------------------- */

  setupTooltips() {
    this.items = Array.from(this.querySelectorAll('.tag-cloud__item'));
    this.triggers = Array.from(this.querySelectorAll('[data-tc-trigger]'));
    if (this.triggers.length === 0) return;

    this.hoverCapable = window.matchMedia('(hover: hover)').matches;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    // Hover devices rely on CSS; only touch devices need the tap toggle.
    if (!this.hoverCapable) {
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onTriggerClick));
      document.addEventListener('click', this.onDocumentClick);
    }
    this.addEventListener('keydown', this.onKeydown);
  }

  onTriggerClick(event) {
    event.preventDefault();
    const item = event.currentTarget.closest('.tag-cloud__item');
    const willOpen = !item.classList.contains('is-open');
    this.closeAll();
    if (willOpen) this.open(item);
  }

  open(item) {
    item.classList.add('is-open');
    const trigger = item.querySelector('[data-tc-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  closeAll() {
    this.items.forEach((item) => {
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-tc-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  onDocumentClick(event) {
    // Close on any tap that lands off the open tooltip. Taps on a trigger are
    // handled by onTriggerClick (toggle), and taps on the tooltip content keep
    // it open; everything else — elsewhere in the section or off it — closes.
    if (event.target.closest('[data-tc-trigger]') || event.target.closest('[data-tc-tooltip]')) {
      return;
    }
    this.closeAll();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeAll();
  }

  /* Lightbox --------------------------------------------------------------- */

  setupLightbox() {
    this.lightbox = this.querySelector('[data-tc-lightbox]');
    this.lightboxOpeners = Array.from(this.querySelectorAll('[data-tc-lightbox-open]'));
    if (!this.lightbox || this.lightboxOpeners.length === 0) return;

    this.openLightbox = this.openLightbox.bind(this);
    this.closeLightbox = this.closeLightbox.bind(this);
    this.onLightboxKeydown = this.onLightboxKeydown.bind(this);

    this.lightboxOpeners.forEach((button) => button.addEventListener('click', this.openLightbox));
    this.lightbox
      .querySelectorAll('[data-tc-lightbox-close]')
      .forEach((button) => button.addEventListener('click', this.closeLightbox));
    document.addEventListener('keydown', this.onLightboxKeydown);
  }

  openLightbox() {
    this.lightboxReturnFocus = document.activeElement;
    this.lightbox.hidden = false;
    // Lock the page behind the modal so only the image scrolls.
    document.documentElement.classList.add('tag-cloud-lightbox-open');
    const closeButton = this.lightbox.querySelector('.tag-cloud__lightbox-close');
    if (closeButton) closeButton.focus();
  }

  closeLightbox() {
    if (this.lightbox.hidden) return;
    this.lightbox.hidden = true;
    document.documentElement.classList.remove('tag-cloud-lightbox-open');
    if (this.lightboxReturnFocus && typeof this.lightboxReturnFocus.focus === 'function') {
      this.lightboxReturnFocus.focus();
    }
  }

  onLightboxKeydown(event) {
    if (event.key === 'Escape' && !this.lightbox.hidden) this.closeLightbox();
  }
}

customElements.define('tag-cloud', TagCloud);
